import {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {SVG} from '../../../../../../../constants/assetLists';
import {PERMISSIONS_LIST} from '../../../data/permissionsList';
import {
  createDefaultRole,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  RoleFormValues,
} from '../../../types';
import RoleRow from './RoleRow';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {getManualRoles, getTemplateRoles} from './defaultRoles';
import {canDeleteRole, getRoleDisplayName} from './roleReferenceUtils';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import AppButton from '../../../../../../../components/common/AppButton';
import {ICONS} from '../../../../../../users/data';

const FIXED_COLUMNS = [
  {label: 'מחלקה', width: 200},
  {label: 'תפקידים', width: 400},
  {label: 'דרגת הרשאות', width: 220},
  {label: 'הרשאות מותאמות אישית', width: 72},
] as const;

const PERMISSION_COLUMN_WIDTH = 72;
const GROUP_HEADER_ROW_HEIGHT = 32;

const headCellBaseClass = 'px-2';
const headerTextClass = 'text-sm text-black font-normal';
const UNASSIGNED_ROLES_SECTION_ID = '__unassigned_roles__';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function Roles({selectedFieldPath}: Props) {
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [openMenuRoleId, setOpenMenuRoleId] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingAssignDepts, setPendingAssignDepts] = useState<
    Record<string, string>
  >({});
  const initializedPathRef = useRef(false);
  const {getDefaultSelectedNode, allRoleTypes} = useOnBoardingContext();
  const defaultSelectedNode = getDefaultSelectedNode(selectedFieldPath);

  const {control, watch, getValues} =
    useFormContext<NetworkDetailsFormValues>();
  const {showSnackbar} = useSnackbar();
  const network = watch('network') ?? [];

  const departments = watch(`${selectedFieldPath}.departments`) ?? [];
  const roles = (watch(`${selectedFieldPath}.roles`) ?? []) as RoleFormValues[];
  const departmentIdsKey = departments
    .map(department => department.id)
    .join('|');

  const {
    fields: roleFields,
    append: appendRole,
    remove: removeRole,
    replace,
    update: updateRole,
  } = useFieldArray({
    control,
    name: `${selectedFieldPath}.roles`,
    keyName: 'id',
  });

  const unassignedRoles = useMemo(
    () =>
      roles
        .map((role, index) => ({role, index}))
        .filter(({role}) => !role.departmentId),
    [roles],
  );

  const departmentOptions = useMemo(
    () =>
      departments.map(dept => ({
        label: dept.name,
        value: dept.id,
        icon: ICONS?.find(icon => icon.id === dept.icon)?.icon,
      })),
    [departments],
  );

  useEffect(() => {
    setExpandedSet(new Set(departments.map(dept => dept.id)));
  }, [departmentIdsKey, defaultSelectedNode.role]);

  useEffect(() => {
    if (unassignedRoles.length > 0) {
      setExpandedSet(prev => new Set([...prev, UNASSIGNED_ROLES_SECTION_ID]));
    }
  }, [unassignedRoles.length]);

  useEffect(() => {
    if (initializedPathRef.current) return;

    if (roleFields.length === 0) {
      replace(
        defaultSelectedNode.role === 'manual'
          ? getManualRoles(departments)
          : getTemplateRoles(departments, allRoleTypes?.data),
      );
    }

    initializedPathRef.current = true;
  }, [
    departments,
    defaultSelectedNode.role,
    replace,
    roleFields.length,
    selectedFieldPath,
    allRoleTypes,
  ]);

  const getRolesForDepartment = (departmentId: string) =>
    roles
      .map((role, index) => ({role, index}))
      .filter(({role}) => role.departmentId === departmentId);

  const handleMenuOpen = (
    roleId: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setOpenMenuRoleId(roleId);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setOpenMenuRoleId(null);
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (roleIndex: number, value: string) => {
    if (value === 'delete') {
      const role = getValues(
        `${selectedFieldPath}.roles.${roleIndex}`,
      ) as RoleFormValues;

      const result = canDeleteRole(
        network,
        role.id,
        getRoleDisplayName(role.roleType, allRoleTypes?.data),
      );
      if (!result.allowed) {
        showSnackbar({
          message: result.message,
          type: 'error',
          duration: 5000,
        });
        handleMenuClose();
        return;
      }

      removeRole(roleIndex);
    } else if (value === 'copy') {
      const role = getValues(
        `${selectedFieldPath}.roles.${roleIndex}`,
      ) as RoleFormValues;
      appendRole({
        ...role,
        id: crypto.randomUUID(),
      });
    }
    handleMenuClose();
  };

  const handleAddRole = (departmentId: string) => {
    appendRole(createDefaultRole(departmentId));
  };

  const handleAssignRole = (
    roleIndex: number,
    roleId: string,
    departmentId: string,
  ) => {
    if (!departmentId) return;

    const role = getValues(
      `${selectedFieldPath}.roles.${roleIndex}`,
    ) as RoleFormValues;
    updateRole(roleIndex, {...role, departmentId});
    setPendingAssignDepts(prev => {
      const next = {...prev};
      delete next[roleId];
      return next;
    });
    setExpandedSet(prev => new Set([...prev, departmentId]));
  };

  const toggleExpand = (id: string) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="w-full h-full overflow-auto relative">
        <table
          className={`w-full max-h-full table-fixed border-1 border-gray80 border-collapse`}>
          <colgroup>
            {FIXED_COLUMNS.map(column => (
              <col key={column.label} style={{width: `${column.width}px`}} />
            ))}
            {PERMISSIONS_LIST.flatMap((group, groupIdx) =>
              group.permissions.map((permission, permIdx) => (
                <col
                  key={`${group.group}-${permission.id}-${groupIdx}-${permIdx}`}
                  style={{width: `${PERMISSION_COLUMN_WIDTH}px`}}
                />
              )),
            )}
          </colgroup>
          <thead>
            <tr>
              <th
                className={`${headCellBaseClass} sticky top-0 z-[2] h-8 bg-white`}
                colSpan={FIXED_COLUMNS.length}
              />

              {PERMISSIONS_LIST.map((group, groupIndex) => (
                <th
                  key={group.group}
                  className={`${headCellBaseClass} sticky top-0 z-[2] h-8 border-s border-s-gray80 ${
                    groupIndex % 2 === 0 ? 'bg-gray70' : 'bg-gray80'
                  }`}
                  colSpan={group.permissions.length}>
                  <span className={headerTextClass}>{group.name}</span>
                </th>
              ))}
            </tr>

            <tr>
              {FIXED_COLUMNS.map(column => (
                <th
                  key={column.label}
                  className={`${headCellBaseClass} sticky z-[1] transition-colors duration-200 bg-gray95`}
                  style={{
                    top: GROUP_HEADER_ROW_HEIGHT,
                    width: `${column.width}px`,
                    maxWidth: `${column.width}px`,
                  }}>
                  <span className={headerTextClass}>{column.label}</span>
                </th>
              ))}

              {PERMISSIONS_LIST.flatMap((group, groupIdx) =>
                group.permissions.map((permission, idx) => (
                  <th
                    key={`${group.group}-${permission.id}-${groupIdx}-${idx}`}
                    className={`${headCellBaseClass} sticky z-[1] transition-colors duration-200 bg-gray95 text-center ${idx === 0 ? 'border-s border-s-gray80' : ''}`}
                    style={{
                      top: GROUP_HEADER_ROW_HEIGHT,
                      width: `${PERMISSION_COLUMN_WIDTH}px`,
                      maxWidth: `${PERMISSION_COLUMN_WIDTH}px`,
                    }}>
                    <span className={headerTextClass}>{permission.name}</span>
                  </th>
                )),
              )}
            </tr>
          </thead>

          <tbody>
            {unassignedRoles.length > 0 && (
              <Fragment key={UNASSIGNED_ROLES_SECTION_ID}>
                <tr>
                  <td colSpan={4} className="h-10 px-2 py-1">
                    <div className="h-full flex items-center gap-2">
                      <button
                        type="button"
                        className="w-[12px] h-[12px]"
                        onClick={() =>
                          toggleExpand(UNASSIGNED_ROLES_SECTION_ID)
                        }>
                        <img
                          src={
                            expandedSet.has(UNASSIGNED_ROLES_SECTION_ID)
                              ? SVG.expandIcon
                              : SVG.lessThanIcon
                          }
                          alt="caret"
                          className="w-full h-full object-contain"
                        />
                      </button>

                      <span className="text-sm text-black">
                        תפקידים לא משויכים
                      </span>

                      <span className="text-sm text-gray">
                        בחר מחלקה עבור תפקיד זה, או הסר את התפקיד. תוכל ליצור
                        מחלקות חדשות בכל עת שתצטרך.
                      </span>
                    </div>
                  </td>

                  {PERMISSIONS_LIST.map(group => (
                    <td
                      key={group.group}
                      colSpan={group.permissions.length}
                      className="h-10 px-2 py-1 border-s border-s-gray80"
                    />
                  ))}
                </tr>

                {expandedSet.has(UNASSIGNED_ROLES_SECTION_ID) &&
                  unassignedRoles.map(({role, index: roleIndex}) => {
                    const selectedDeptId = pendingAssignDepts[role.id] ?? '';
                    const selectedDeptOption =
                      departmentOptions.find(
                        opt => opt.value === selectedDeptId,
                      ) ?? null;

                    return (
                      <Fragment key={role.id}>
                        <RoleRow
                          selectedFieldPath={selectedFieldPath}
                          roleIndex={roleIndex}
                          openMenuRoleId={openMenuRoleId}
                          menuAnchorEl={menuAnchorEl}
                          onMenuOpen={handleMenuOpen}
                          onMenuClose={handleMenuClose}
                          onMenuItemClick={handleMenuItemClick}
                          displayBorder={false}
                        />

                        <tr>
                          <td
                            colSpan={4}
                            className="h-10 border-b border-b-gray70 px-2 py-1">
                            <div className="h-full flex items-center gap-3">
                              <div className="flex-1 max-w-[378px]">
                                <AppMUIAutocomplete
                                  disablePortal={false}
                                  placeholder={
                                    selectedDeptOption ? '' : 'מחלקה'
                                  }
                                  options={departmentOptions}
                                  size="small"
                                  fullWidth
                                  getOptionLabel={option => option.label}
                                  id={`unassigned-role-department-${role.id}`}
                                  renderValue={() => {
                                    const selected = departmentOptions.find(
                                      opt => opt.value === selectedDeptId,
                                    );
                                    return selected ? (
                                      <div className="flex items-center">
                                        <img
                                          src={selected.icon}
                                          alt=""
                                          className="w-[18px] h-[18px] object-contain me-2"
                                        />
                                        {selected.label}
                                      </div>
                                    ) : null;
                                  }}
                                  renderOption={(props, option) => {
                                    const {key, ...rest} = props;
                                    return (
                                      <li key={key} {...rest}>
                                        <img
                                          src={option.icon}
                                          alt=""
                                          className="w-[18px] h-[18px] object-contain me-2"
                                        />
                                        {option.label}
                                      </li>
                                    );
                                  }}
                                  value={selectedDeptOption}
                                  onChange={(_, newValue) => {
                                    setPendingAssignDepts(prev => ({
                                      ...prev,
                                      [role.id]:
                                        (
                                          newValue as
                                            | (typeof departmentOptions)[0]
                                            | null
                                        )?.value ?? '',
                                    }));
                                  }}
                                />
                              </div>

                              <AppButton
                                variant="secondary"
                                disabled={!selectedDeptId}
                                onClick={() =>
                                  handleAssignRole(
                                    roleIndex,
                                    role.id,
                                    selectedDeptId,
                                  )
                                }>
                                שיוך
                              </AppButton>
                            </div>
                          </td>

                          {PERMISSIONS_LIST.map(group => (
                            <td
                              key={group.group}
                              colSpan={group.permissions.length}
                              className="h-10 px-2 py-1 border-b border-b-gray70 border-s border-s-gray80"
                            />
                          ))}
                        </tr>
                      </Fragment>
                    );
                  })}
              </Fragment>
            )}

            {departments.map(dept => (
              <Fragment key={dept.id}>
                <tr>
                  <td colSpan={4} className="h-10 px-2 py-1">
                    <div className="h-full flex items-center gap-2">
                      <button
                        type="button"
                        className="w-[12px] h-[12px]"
                        onClick={() => toggleExpand(dept.id)}>
                        <img
                          src={
                            expandedSet.has(dept.id)
                              ? SVG.expandIcon
                              : SVG.lessThanIcon
                          }
                          alt="caret"
                          className="w-full h-full object-contain"
                        />
                      </button>

                      <span className="text-sm text-black">{dept.name}</span>
                    </div>
                  </td>

                  {PERMISSIONS_LIST.map(group => (
                    <td
                      key={group.group}
                      colSpan={group.permissions.length}
                      className="h-10 px-2 py-1 border-s border-s-gray80"
                    />
                  ))}
                </tr>

                {expandedSet.has(dept.id) &&
                  getRolesForDepartment(dept.id).map(
                    ({role, index: roleIndex}) => (
                      <RoleRow
                        key={role.id}
                        selectedFieldPath={selectedFieldPath}
                        roleIndex={roleIndex}
                        openMenuRoleId={openMenuRoleId}
                        menuAnchorEl={menuAnchorEl}
                        onMenuOpen={handleMenuOpen}
                        onMenuClose={handleMenuClose}
                        onMenuItemClick={handleMenuItemClick}
                      />
                    ),
                  )}

                <tr>
                  <td colSpan={4} className="h-10 px-2 py-1">
                    <div className="h-full flex items-center">
                      <button
                        type="button"
                        className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
                        onClick={() => handleAddRole(dept.id)}>
                        <img
                          src={SVG.addDarkBlueIcon}
                          alt="add"
                          className="w-[14px] h-[14px] object-contain"
                        />
                      </button>
                    </div>
                  </td>

                  {PERMISSIONS_LIST.map(group => (
                    <td
                      key={group.group}
                      colSpan={group.permissions.length}
                      className="h-10 px-2 py-1 border-s border-s-gray80"
                    />
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Roles;
